// @ts-strict

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { dropdownValues } from '@app/core/helpers/constants';
import { checkDateFormat } from '@app/core/helpers/tools';
import { DefaultApiType } from '@app/core/models/core-models';
import { environment } from '@env/environment';
import { FilterMenuOptions, GraphFilterOption, StatisticsColumnManagmentNumberType, StatsRequestParams } from '@pages/statistics/shared/interfaces/statistics';
import { StatisticsMapsGraphService } from '@pages/statistics/shared/statistics-graphs/statistics-maps-graph/statistics-maps-graph.service';
import { AvailableStatisticsTableColumns } from '@pages/statistics/shared/statistics-table/table.service';
import { StatisticsService } from '@pages/statistics/statistics.service';
import { StatisticsGraphsService } from '@services/statistics/statistics-graphs.service';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { PpiStatisticsGraphResponse, PpiStatisticsMapResponse, PpiStatisticsResponse } from './interfaces/ppi-statistics';

@Injectable({
  providedIn: 'root'
})
export class PpiStatisticsService {
  public defaultVisibleColumns: AvailableStatisticsTableColumns[] = [
    'configurationName',
    'leads',
    'leadToPay',
    'leadAccepted',
    'leadPending',
    'leadRejected',
    'earnings',
    'earningsToPay',
    'earningsAccepted',
    'earningsPending',
    'earningsRejected',
    'uniqueVisits',
    'conversion',
    'epc'
  ];

  public initialRequestParams: StatsRequestParams = {
    ...this.statisticsService.getInitialRequestDate(),
    order_field: 'program_id',
    order_type: 'desc',
    per_page: 50
  };

  public columnManagementNumbers: StatisticsColumnManagmentNumberType = {
    leads: [0, 1, 2, 3, 4],
    earnings: [5, 6, 7, 8, 9],
    others: [10, 11, 26, 12, 13, 14, 27, 28, 18, 19]
  };

  public dropdownValues = [
    dropdownValues[12],
    dropdownValues[13],
    dropdownValues[14],
    dropdownValues[15],
    dropdownValues[41],
    dropdownValues[42],
    dropdownValues[51],
    dropdownValues[52],
    dropdownValues[53],
    dropdownValues[54],
    dropdownValues[43],
    dropdownValues[44],
    dropdownValues[45],
    dropdownValues[46],
    dropdownValues[47],
    dropdownValues[48],
    dropdownValues[20],
    dropdownValues[21]
  ];

  public requestParamsMap = {
    campaignMenu: 'programs[]',
    configurationNameMenu: 'configurations[]',
    countriesMenu: 'countries[]',
    status: 'statuses[]',
    countries: 'countries[]',
    earns: 'earnings',
    visits: 'visits',
    conversion: 'conversion',
    acceptance_factor: 'acceptance',
    epc: 'epc',
    rate: 'rate'
  };

  public graphFilterOptions: GraphFilterOption[] = [
    {
      id: 0,
      name: 'leads',
      translation: 'leads',
      status: true
    },
    {
      id: 1,
      name: 'earnings_status_to_payment',
      translation: 'withdrawal_commissions',
      status: false
    },
    {
      id: 2,
      name: 'earnings_status_payment_waiting',
      translation: 'accepted_commissions',
      status: false
    },
    {
      id: 3,
      name: 'earnings_status_wait',
      translation: 'pending_commissions',
      status: false
    },
    {
      id: 4,
      name: 'earnings_status_rejected',
      translation: 'rejected_commissions',
      status: false
    },
    {
      id: 5,
      name: 'visits',
      translation: 'visits',
      status: false
    },
    {
      id: 6,
      name: 'conversion',
      translation: 'conversion',
      status: false
    }
  ];

  public filterMenuOptions: FilterMenuOptions = {
    campaignMenu: [
      {
        category: 'sort',
        values: [dropdownValues[12], dropdownValues[13]]
      },
      {
        category: 'searchInput',
        name: 'campaign_id',
        searchMethod: 'id',
        values: null
      }
    ],
    configurationNameMenu: [
      {
        category: 'sort',
        values: [dropdownValues[14], dropdownValues[15]]
      },
      {
        category: 'searchInput',
        name: 'config_id',
        searchMethod: 'id',
        values: null
      }
    ],
    leadsMenu: [
      {
        category: 'sort',
        values: [dropdownValues[53], dropdownValues[54]]
      },
      {
        category: 'slider',
        name: 'leads',
        rangeStep: 1,
        minValue: 0,
        maxValue: 2000,
        currentMinValue: 0,
        currentMaxValue: 2000,
        fieldType: 'slider',
        paramFrom: 'leads_from',
        paramTo: 'leads_to',
        unit: ''
      }
    ],
    rateMenu: [
      {
        category: 'sort',
        values: [dropdownValues[20], dropdownValues[21]]
      }
    ],
    earnsMenu: [
      {
        category: 'sort',
        values: [dropdownValues[51], dropdownValues[52]]
      },
      {
        category: 'slider',
        name: 'earns',
        rangeStep: 0.01,
        minValue: 0,
        maxValue: 2000,
        currentMinValue: 0,
        currentMaxValue: 2000,
        fieldType: 'slider',
        paramFrom: 'earnings_from',
        paramTo: 'earnings_to',
        unit: 'currency'
      }
    ],
    countriesMenu: [
      {
        category: 'countriesSelect',
        name: 'countries',
        values: []
      }
    ],
    statusMenu: [
      {
        category: 'checkboxesList',
        values: {
          status: [
            {
              id: 2,
              text: 'accepted',
              checked: true,
              noTranslation: false,
              category: 'status',
              fieldType: 'checkbox'
            },
            {
              id: 1,
              text: 'pending',
              checked: true,
              noTranslation: false,
              category: 'status',
              fieldType: 'checkbox'
            },
            {
              id: 3,
              text: 'rejected',
              checked: true,
              noTranslation: false,
              category: 'status',
              fieldType: 'checkbox'
            }
          ]
        }
      }
    ],
    visitsMenu: [
      {
        category: 'sort',
        values: [dropdownValues[41], dropdownValues[42]]
      },
      {
        category: 'slider',
        name: 'visits',
        rangeStep: 1,
        minValue: 0,
        maxValue: 2000,
        currentMinValue: 0,
        currentMaxValue: 2000,
        fieldType: 'slider',
        paramFrom: 'visits_from',
        paramTo: 'visits_to'
      }
    ],
    conversionMenu: [
      {
        category: 'sort',
        values: [dropdownValues[43], dropdownValues[44]]
      },
      {
        category: 'slider',
        name: 'conversion',
        rangeStep: 0.1,
        minValue: 0,
        maxValue: 100,
        currentMinValue: 0,
        currentMaxValue: 100,
        fieldType: 'slider',
        paramFrom: 'conversion_from',
        paramTo: 'conversion_to',
        unit: '%'
      }
    ],
    acceptanceMenu: [
      {
        category: 'sort',
        values: [dropdownValues[45], dropdownValues[46]]
      },
      {
        category: 'slider',
        name: 'acceptance',
        rangeStep: 0.1,
        minValue: 0,
        maxValue: 100,
        currentMinValue: 0,
        currentMaxValue: 100,
        fieldType: 'slider',
        paramFrom: 'acceptance_from',
        paramTo: 'acceptance_to',
        unit: '%'
      }
    ],
    epcMenu: [
      {
        category: 'sort',
        values: [dropdownValues[47], dropdownValues[48]]
      },
      {
        category: 'slider',
        name: 'epc',
        rangeStep: 0.01,
        minValue: 0,
        maxValue: 2000,
        currentMinValue: 0,
        currentMaxValue: 2000,
        fieldType: 'slider',
        paramFrom: 'epc_from',
        paramTo: 'epc_to',
        unit: 'currency'
      }
    ]
  };

  constructor(
    private readonly http: HttpClient,
    private readonly statisticsService: StatisticsService,
    private readonly statGraphsService: StatisticsGraphsService,
    private readonly statisticsMapsGraphService: StatisticsMapsGraphService
  ) {}

  public getPPIStatisticsData = (requestParams: StatsRequestParams): Observable<{ error: boolean } & Required<PpiStatisticsResponse>> => {
    const params = {
      ...requestParams,
      date_from: checkDateFormat(requestParams.date_from),
      date_to: checkDateFormat(requestParams.date_to)
    };

    return this.http.get<DefaultApiType<PpiStatisticsResponse>>(environment.apiUrl + `/statistics/ppi`, { params }).pipe(
      map(({ data, status_code }) => {
        if (status_code && status_code === 200 && data && data.pagination && data.ppi && data.summary) {
          const { pagination, ppi, summary } = data;

          return { error: false, pagination, ppi, summary };
        }

        return { error: true, pagination: {}, ppi: [], summary: [] };
      })
    );
  };

  public getPPIStatisticsMapData = (requestParams: StatsRequestParams): Observable<{ error: boolean } & Required<PpiStatisticsMapResponse>> => {
    this.statisticsMapsGraphService.setIsLoading(true);

    const { order_field, order_type, page, ...rest } = requestParams;

    const params = {
      ...rest,
      date_from: checkDateFormat(requestParams.date_from),
      date_to: checkDateFormat(requestParams.date_to)
    };

    return this.http.get<DefaultApiType<PpiStatisticsMapResponse>>(environment.apiUrl + `/statistics/ppi/map`, { params }).pipe(
      map(({ data, status_code }) => {
        if (status_code && status_code === 200 && data && data.map_statistics) {
          const { map_statistics } = data;

          return { error: false, map_statistics };
        }

        return { error: true, map_statistics: [] };
      }),
      finalize(() => this.statisticsMapsGraphService.setIsLoading(false))
    );
  };

  public getPPIStatisticsGraphData = (requestParams: StatsRequestParams): Observable<{ error: boolean } & Required<PpiStatisticsGraphResponse>> => {
    this.statGraphsService._loadingGraphDataSubject.next(true);

    const { order_field, order_type, page, ...rest } = requestParams;

    const params = {
      ...rest,
      date_from: checkDateFormat(requestParams.date_from),
      date_to: checkDateFormat(requestParams.date_to)
    };

    return this.http.get<DefaultApiType<PpiStatisticsGraphResponse>>(environment.apiUrl + `/statistics/ppi/chart`, { params }).pipe(
      map(({ data, status_code }) => {
        if (status_code && status_code === 200 && data && data.chart_statistics) {
          const { chart_statistics } = data;

          return { error: false, chart_statistics };
        }

        return { error: true, chart_statistics: [] };
      }),
      finalize(() => this.statGraphsService._loadingGraphDataSubject.next(false))
    );
  };
}
